import { RefreshCw, X } from 'lucide-react'
import { useState, type RefCallback } from 'react'
import { useDraggableToast } from '@/hooks/useDraggableToast'
import { useSoftwareUpdateCheck } from './useSoftwareUpdateCheck'

/**
 * Angular `app.component` software-update toast — polls `/commit.txt`, shows
 * compact card with Update Now + auto-refresh countdown.
 * Draggable so it can be moved when covering page actions.
 * Later / × minimize to a chip so the notice can be reopened until refresh.
 */
export function SoftwareUpdateBanner() {
  const { updateAvailable, remainingLabel, applyUpdate } = useSoftwareUpdateCheck()
  const [minimized, setMinimized] = useState(false)
  const { dragHandlers, didDrag, isPositioned } = useDraggableToast()
  const { ref, style, ...pointerHandlers } = dragHandlers
  const draggedClass = isPositioned ? ' software-update-toast--dragged' : ''

  if (!updateAvailable) return null

  if (minimized) {
    return (
      <button
        type="button"
        ref={ref as RefCallback<HTMLButtonElement>}
        style={style}
        className={`software-update-toast software-update-toast--chip${draggedClass}`}
        {...pointerHandlers}
        onClick={() => {
          if (!didDrag()) setMinimized(false)
        }}
        aria-label={`Refresh. Auto-refresh in ${remainingLabel}. Drag to move, click to expand.`}
        data-testid="software-update-banner-chip"
        title="Drag to move · click to expand"
      >
        <RefreshCw className="software-update-toast__chip-icon" aria-hidden="true" />
        <span>
          Refresh · <strong>{remainingLabel}</strong>
        </span>
      </button>
    )
  }

  return (
    <div
      ref={ref as RefCallback<HTMLDivElement>}
      style={style}
      className={`software-update-toast${draggedClass}`}
      role="status"
      aria-live="polite"
      data-testid="software-update-banner"
      title="Drag to move"
      {...pointerHandlers}
    >
      <div className="software-update-toast__top">
        <RefreshCw className="software-update-toast__lead-icon" aria-hidden="true" />
        <div className="software-update-toast__content">
          <p className="software-update-toast__title">Update available</p>
          <p className="software-update-toast__body">
            Reload when you&apos;re free, or update now for the latest version.
          </p>
          <p className="software-update-toast__timer">
            Auto-refresh in <strong>{remainingLabel}</strong>
          </p>
        </div>
        <button
          type="button"
          className="software-update-toast__dismiss"
          data-no-drag
          onClick={() => setMinimized(true)}
          aria-label="Minimize update notice"
          data-testid="software-update-dismiss"
        >
          <X />
        </button>
      </div>

      <div className="software-update-toast__actions">
        <button
          type="button"
          className="software-update-toast__btn software-update-toast__btn--primary"
          data-no-drag
          onClick={applyUpdate}
          data-testid="software-update-now"
        >
          Update now
        </button>
        <button
          type="button"
          className="software-update-toast__btn software-update-toast__btn--secondary"
          data-no-drag
          onClick={() => setMinimized(true)}
          data-testid="software-update-later"
        >
          Later
        </button>
      </div>
    </div>
  )
}
